import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Bell, Plus, Trash2, Power, Search, ChevronRight, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { slug: '', name: 'Tutte le categorie' },
  { slug: 'ultraleggeri', name: 'Ultraleggeri' },
  { slug: 'propulsione-singola', name: 'Propulsione Singola' },
  { slug: 'propulsione-multipla', name: 'Propulsione Multipla' },
  { slug: 'turboelica', name: 'Turboelica' },
  { slug: 'jet', name: 'Jet' },
  { slug: 'elicotteri', name: 'Elicotteri' },
];

export default function Favorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({ name: '', category: '', manufacturer: '', max_price: '', location: '', email_notify: true });
  const [matchesOpen, setMatchesOpen] = useState(null);
  const [matchListings, setMatchListings] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/favorites`, { withCredentials: true })
      .then(r => setFavorites(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/alerts`, { withCredentials: true })
      .then(r => setAlerts(r.data)).catch(() => {}).finally(() => setAlertsLoading(false));
  }, [user]);

  const removeFavorite = async (listingId) => {
    await axios.post(`${API}/favorites/${listingId}`, {}, { withCredentials: true });
    setFavorites(prev => prev.filter(f => f.listing_id !== listingId));
  };

  const createAlert = async () => {
    const payload = {
      ...alertForm,
      max_price: alertForm.max_price ? parseInt(alertForm.max_price) : null,
    };
    const res = await axios.post(`${API}/alerts`, payload, { withCredentials: true });
    setAlerts(prev => [res.data, ...prev]);
    setCreateOpen(false);
    setAlertForm({ name: '', category: '', manufacturer: '', max_price: '', location: '', email_notify: true });
  };

  const toggleAlert = async (alertId) => {
    const res = await axios.put(`${API}/alerts/${alertId}/toggle`, {}, { withCredentials: true });
    setAlerts(prev => prev.map(a => a.alert_id === alertId ? { ...a, active: res.data.active } : a));
  };

  const deleteAlert = async (alertId) => {
    await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
    setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
  };

  const viewMatches = async (alert) => {
    setMatchesOpen(alert);
    const res = await axios.get(`${API}/alerts/${alert.alert_id}/matches`, { withCredentials: true });
    setMatchListings(res.data);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Accedi per visualizzare i preferiti</p>
          <Link to="/login" className="text-[#C5A059] hover:underline">Accedi</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-2">I Tuoi Velivoli</p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Preferiti & Avvisi
          </h1>
        </div>

        <Tabs defaultValue="favorites">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="favorites" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 text-xs sm:text-sm" data-testid="tab-favorites">
              <Heart size={13} className="mr-1.5" /> Preferiti ({favorites.length})
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 text-xs sm:text-sm" data-testid="tab-alerts">
              <Bell size={13} className="mr-1.5" /> Ricerche Salvate ({alerts.length})
            </TabsTrigger>
          </TabsList>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : favorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="favorites-grid">
                {favorites.map(l => (
                  <ListingCard key={l.listing_id} listing={l} isFavorited={true} onToggleFavorite={removeFavorite} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20" data-testid="favorites-empty">
                <Heart size={32} className="mx-auto mb-4 text-zinc-700" />
                <p className="text-lg text-zinc-400 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Nessun preferito</p>
                <p className="text-sm text-zinc-600 mb-6">Salva gli annunci che ti interessano per ritrovarli facilmente</p>
                <Link to="/listings" className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="explore-listings-btn">
                  Esplora Annunci
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-zinc-400">Ricevi una notifica quando un nuovo annuncio corrisponde ai tuoi criteri.</p>
              <button onClick={() => setCreateOpen(true)} className="flex items-center gap-2 bg-[#C5A059] text-black px-4 py-2 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="create-alert-btn">
                <Plus size={14} /> Nuova Ricerca
              </button>
            </div>

            {alertsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : alerts.length > 0 ? (
              <div className="space-y-3" data-testid="alerts-list">
                {alerts.map(alert => (
                  <div key={alert.alert_id} className={`bg-zinc-900 border p-4 flex items-center gap-4 ${alert.active ? 'border-zinc-800' : 'border-zinc-800/50 opacity-60'}`} data-testid={`alert-${alert.alert_id}`}>
                    <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${alert.active ? 'bg-[#C5A059]/10 text-[#C5A059]' : 'bg-zinc-800 text-zinc-600'}`}>
                      <Bell size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">{alert.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        {alert.category && <span className="text-[10px] font-mono uppercase tracking-wider text-[#C5A059]">{alert.category.replace('-',' ')}</span>}
                        {alert.manufacturer && <span className="text-[10px] text-zinc-500">{alert.manufacturer}</span>}
                        {alert.max_price && <span className="text-[10px] text-zinc-500 font-mono">&le; {alert.max_price.toLocaleString('it-IT')}EUR</span>}
                        {alert.location && <span className="text-[10px] text-zinc-500">{alert.location}</span>}
                      </div>
                      <p className="text-[10px] text-zinc-600 mt-1">
                        {alert.matches || 0} corrispondenze &middot; {alert.email_notify ? 'Email attiva' : 'Solo notifiche'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => viewMatches(alert)} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-[#C5A059] hover:border-[#C5A059]/50 transition-colors" title="Vedi corrispondenze" data-testid={`view-matches-${alert.alert_id}`}>
                        <Search size={13} />
                      </button>
                      <button onClick={() => toggleAlert(alert.alert_id)} className={`w-8 h-8 flex items-center justify-center border transition-colors ${alert.active ? 'border-emerald-700/50 text-emerald-400 hover:border-emerald-500' : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'}`} title={alert.active ? 'Disattiva' : 'Attiva'} data-testid={`toggle-alert-${alert.alert_id}`}>
                        <Power size={13} />
                      </button>
                      <button onClick={() => deleteAlert(alert.alert_id)} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-400/50 transition-colors" title="Elimina" data-testid={`delete-alert-${alert.alert_id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20" data-testid="alerts-empty">
                <Bell size={32} className="mx-auto mb-4 text-zinc-700" />
                <p className="text-lg text-zinc-400 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Nessuna ricerca salvata</p>
                <p className="text-sm text-zinc-600 mb-6">Crea un avviso per ricevere notifiche quando nuovi annunci corrispondono ai tuoi criteri</p>
                <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="create-alert-empty-btn">
                  <Plus size={14} /> Crea Avviso
                </button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Alert Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="create-alert-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Bell size={18} className="text-[#C5A059]" /> Nuova Ricerca Salvata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Nome avviso</Label>
              <Input value={alertForm.name} onChange={e => setAlertForm(p => ({...p, name: e.target.value}))} placeholder="Es: Ultraleggeri sotto 50k" className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="alert-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Categoria</Label>
              <Select value={alertForm.category} onValueChange={v => setAlertForm(p => ({...p, category: v === 'all' ? '' : v}))}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-9 text-xs" data-testid="alert-category"><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.slug || 'all'} value={c.slug || 'all'} className="text-zinc-300 text-xs">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Costruttore</Label>
              <Input value={alertForm.manufacturer} onChange={e => setAlertForm(p => ({...p, manufacturer: e.target.value}))} placeholder="Es: Cessna, Piper, Diamond..." className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="alert-manufacturer" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Prezzo massimo (EUR)</Label>
              <Input type="number" value={alertForm.max_price} onChange={e => setAlertForm(p => ({...p, max_price: e.target.value}))} placeholder="Es: 150000" className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="alert-max-price" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Posizione</Label>
              <Input value={alertForm.location} onChange={e => setAlertForm(p => ({...p, location: e.target.value}))} placeholder="Es: Milano, Roma, Brescia..." className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="alert-location" />
            </div>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${alertForm.email_notify ? 'bg-[#C5A059] border-[#C5A059]' : 'border-zinc-700 bg-zinc-800 group-hover:border-zinc-500'}`}>
                {alertForm.email_notify && <span className="text-black text-xs font-bold">&#10003;</span>}
              </div>
              <input type="checkbox" checked={alertForm.email_notify} onChange={e => setAlertForm(p => ({...p, email_notify: e.target.checked}))} className="hidden" />
              <span className="text-xs text-zinc-300">Notifica via email</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setCreateOpen(false)} className="px-5 py-2.5 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors">Annulla</button>
              <button onClick={createAlert} className="flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="alert-save-btn">
                <Bell size={14} /> Salva Avviso
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Matches Dialog */}
      <Dialog open={!!matchesOpen} onOpenChange={(open) => { if (!open) { setMatchesOpen(null); setMatchListings([]); }}}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="matches-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Search size={18} className="text-[#C5A059]" /> Corrispondenze: {matchesOpen?.name}
            </DialogTitle>
          </DialogHeader>
          {matchListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {matchListings.map(l => (
                <ListingCard key={l.listing_id} listing={l} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Search size={24} className="mx-auto mb-3 text-zinc-700" />
              <p className="text-sm text-zinc-500">Nessun annuncio corrisponde ai criteri di questa ricerca</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
